"use client";

import * as React from "react";
import { ChevronRight, LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

// ============================================================================
// PromoCard - Gradient border cards for upsells and promotions
// ============================================================================

export interface PromoCardProps {
  /** Card title */
  title: string;
  /** Card description */
  description: string;
  /** Icon component */
  icon?: LucideIcon;
  /** Color theme */
  variant?: "purple" | "amber" | "emerald" | "blue" | "rose";
  /** Primary action button */
  action?: {
    label: string;
    onClick: () => void;
  };
  /** Secondary action */
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  /** Additional className */
  className?: string;
  /** Whether to show dismiss button */
  dismissible?: boolean;
  /** Dismiss callback */
  onDismiss?: () => void;
}

const promoVariants = {
  purple: {
    border: "from-violet-500 via-purple-500 to-fuchsia-500",
    bg: "from-violet-50 to-fuchsia-50 dark:from-violet-950/50 dark:to-fuchsia-950/50",
    iconBg: "from-violet-500 to-purple-600",
    button: "from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700",
    buttonText: "text-white",
  },
  amber: {
    border: "from-amber-400 via-orange-400 to-yellow-500",
    bg: "from-amber-50 to-orange-50 dark:from-amber-950/50 dark:to-orange-950/50",
    iconBg: "from-amber-500 to-orange-500",
    button: "from-amber-100 to-orange-100 hover:from-amber-200 hover:to-orange-200",
    buttonText: "text-amber-800",
  },
  emerald: {
    border: "from-emerald-400 via-teal-400 to-cyan-500",
    bg: "from-emerald-50 to-teal-50 dark:from-emerald-950/50 dark:to-teal-950/50",
    iconBg: "from-emerald-500 to-teal-600",
    button: "from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700",
    buttonText: "text-white",
  },
  blue: {
    border: "from-blue-400 via-indigo-400 to-violet-500",
    bg: "from-blue-50 to-indigo-50 dark:from-blue-950/50 dark:to-indigo-950/50",
    iconBg: "from-blue-500 to-indigo-600",
    button: "from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700",
    buttonText: "text-white",
  },
  rose: {
    border: "from-rose-400 via-pink-400 to-fuchsia-500",
    bg: "from-rose-50 to-pink-50 dark:from-rose-950/50 dark:to-pink-950/50",
    iconBg: "from-rose-500 to-pink-600",
    button: "from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700",
    buttonText: "text-white",
  },
};

export function PromoCard({
  title,
  description,
  icon: Icon,
  variant = "purple",
  action,
  secondaryAction,
  className,
  dismissible = false,
  onDismiss,
}: PromoCardProps) {
  const styles = promoVariants[variant];

  return (
    <div className={cn(
      "relative overflow-hidden rounded-xl",
      `bg-gradient-to-br ${styles.border} p-[1px]`,
      className
    )}>
      <div className="relative rounded-xl bg-white dark:bg-gray-950 p-4">
        {/* Background overlay */}
        <div className={cn(
          "absolute inset-0 opacity-50 rounded-xl",
          `bg-gradient-to-br ${styles.bg}`
        )} />
        
        {/* Dismiss button */}
        {dismissible && onDismiss && (
          <button
            onClick={onDismiss}
            className="absolute top-2 right-2 p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors z-10"
            aria-label="Dismiss"
          >
            <svg className="h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        
        <div className="relative flex items-start gap-4">
          {/* Icon */}
          {Icon && (
            <div className={cn(
              "p-2 rounded-lg shrink-0",
              `bg-gradient-to-br ${styles.iconBg}`
            )}>
              <Icon className="h-5 w-5 text-white" />
            </div>
          )}
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-foreground mb-1">
              {title}
            </h4>
            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
              {description}
            </p>
            
            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              {action && (
                <button
                  onClick={action.onClick}
                  className={cn(
                    "inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all shadow-sm hover:shadow-md",
                    `bg-gradient-to-r ${styles.button} ${styles.buttonText}`
                  )}
                >
                  {action.label}
                  <ChevronRight className="h-4 w-4" />
                </button>
              )}
              {secondaryAction && (
                <button
                  onClick={secondaryAction.onClick}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  {secondaryAction.label}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CompactPromo - Smaller inline promotion banner
// ============================================================================

export interface CompactPromoProps {
  /** Message text */
  message: string;
  /** Action button */
  action?: {
    label: string;
    onClick: () => void;
  };
  /** Color variant */
  variant?: "purple" | "amber" | "emerald" | "blue";
  /** Additional className */
  className?: string;
}

export function CompactPromo({
  message,
  action,
  variant = "purple",
  className,
}: CompactPromoProps) {
  const colors = {
    purple: "from-violet-500 to-purple-600",
    amber: "from-amber-500 to-orange-600",
    emerald: "from-emerald-500 to-teal-600",
    blue: "from-blue-500 to-indigo-600",
  };

  return (
    <div className={cn(
      "flex items-center justify-between gap-4 px-4 py-2 rounded-lg text-white",
      `bg-gradient-to-r ${colors[variant]}`,
      className
    )}>
      <p className="text-sm font-medium truncate">{message}</p>
      {action && (
        <button
          onClick={action.onClick}
          className="shrink-0 px-3 py-1 text-xs font-medium bg-white/20 hover:bg-white/30 rounded-md transition-colors"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

